import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  MatDialog,
  MatDialogRef,
  MatPaginator,
  MatSnackBar,
} from "@angular/material";
import { ConfirmationDialogComponent } from "@app/main/content/confirmation-dialog/confirmation-dialog.component";
import { AdminApi, Emoji, EmojiApi } from "@app/shared/sdk";
import { environment } from "@env/environment";
import { FuseSplashScreenService } from "@fuse/services/splash-screen.service";
import * as _ from "lodash";
import { merge, of as observableOf, Subject } from "rxjs";
import {
  catchError,
  map,
  startWith,
  switchMap,
  takeUntil,
} from "rxjs/operators";
import { ExtraEmojisDatabase } from "../extra-emojis-database";
import { GameSettingsService } from "../game-settings.service";

@Component({
  selector: "all-emojis-container",
  templateUrl: "./all-emojis-container.component.html",
  styleUrls: ["./all-emojis-container.component.scss"],
})
export class AllEmojisContainerComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input("emojis") emojis: Emoji[] = [];
  @Input("editedGameId") editedGameId = "";
  @Input("gameFormat") gameFormat: string = "standard";

  @ViewChild("paginator") paginator: MatPaginator;
  @ViewChild("matPaginator") matPaginator: MatPaginator;

  s3BucketUrl: string = environment.s3BucketUrl;
  extraEmojisDatabase: ExtraEmojisDatabase | null;
  fileNameDialogRef: MatDialogRef<ConfirmationDialogComponent>;

  private _unsubscribeAll: Subject<any>;

  removedEmojisArray = this.gameSettingsService.removedEmojis$;

  removedEmojis: any[] = [];

  constructor(
    public gameSettingsService: GameSettingsService,
    private adminApi: AdminApi,
    private fuseSplashScreenService: FuseSplashScreenService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private emojiApi: EmojiApi
  ) {
    // Set the private defaults
    this._unsubscribeAll = new Subject();

    this.gameSettingsService.getExtraEmojisCount();

    this.removedEmojisArray.subscribe(value => {
      if (value.length !== 0)
        this.removedEmojis = value;
    });
  }

  ngOnInit() {}

  ngAfterViewInit() {
    this.extraEmojisDatabase = new ExtraEmojisDatabase(this.emojiApi);
    merge(this.paginator.page)
      .pipe(
        takeUntil(this._unsubscribeAll),
        startWith({}),
        switchMap(() => {
          this.fuseSplashScreenService.show();
          return this.extraEmojisDatabase!.getData(this.paginator);
        }),
        map((data) => {
          // Flip flag to show that loading has finished.
          this.fuseSplashScreenService.hide();
          return data;
        }),
        catchError(() => {
          this.fuseSplashScreenService.hide();
          return observableOf([]);
        })
      )
      .subscribe((emojis: any[]) => {
        this.emojis = [...emojis];
      });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  uploadEmoji = async ($event) => {
    const files: any[] = $event.target.files;
    let emojis: any[] = [];
    let pageIndex =
      this.paginator.pageIndex === 0 ? 2 : this.paginator.pageIndex + 1;
    this.fuseSplashScreenService.show();

    let uploadPath = this.gameSettingsService.emojiUploadPath;
    if (files && files.length && files.length < 49) {
      for (let file of files) {
        await this.gameSettingsService
          .uploadFile(file, uploadPath, this.adminApi.getCurrentToken().id)
          .then(
            (response) => {
              emojis.push({
                image:
                  response.result.files["file"][0]["providerResponse"].name,
                emojiType: "regular",
                isActive: true,
              });
            },
            (error) => {
              this.fuseSplashScreenService.hide();
              throw new Error(error.message);
            }
          );
      }
      try {
        let response = await this.gameSettingsService
          .upsertEmoji(emojis, "add", pageIndex)
          .toPromise();
        if (!response) {
          this.fuseSplashScreenService.hide();
          throw new Error('Error updating emojis, Please try again later!');
        }
        this.gameSettingsService.extraEmojisLength += emojis.length;
        this.fuseSplashScreenService.hide();
        this.emojis =
          typeof response === "object" ? Object.values(response) : response;
        this.snackBar.open("Emoji added", "OK", {
          verticalPosition: "top",
          duration: 3000,
        });
      } catch (error) {
        this.fuseSplashScreenService.hide();
        this.snackBar.open(error.message, "ERROR", {
          verticalPosition: "top",
          duration: 3000,
        });
        throw new Error(error.message);
      }
    } else {
      alert("Please Select images less then 49");
      this.fuseSplashScreenService.hide();
    }
  };

  /**
   * On populate emojis update screens
   * update is isDuplicationFlag
   */
  populateEmojis() {
    if (this.emojis.length > 47) {
      if (this.editedGameId !== undefined && this.editedGameId !== "") {
        let gameEmojis = [...this.gameSettingsService.extraEmojisSet];
        this.emojis.forEach((el, index) => {
          gameEmojis[index].emojiId = el.id;
          gameEmojis[index].emoji = el;
        });
        this.gameSettingsService.extraEmojisSet = [...gameEmojis];
      } else this.gameSettingsService.extraEmojisSet = this.emojis;

      this.gameSettingsService.populated = true;
      this.gameSettingsService.shufflePrizes(this.emojis);
      this.gameSettingsService.screensEmojis =
        this.gameSettingsService.gameEmojis;
      
      if (_.isUndefined(this.gameSettingsService.routeParams.id)) {
        localStorage.setItem("emojis", JSON.stringify(this.emojis));
      }
      this.gameSettingsService.isDuplicationFlag = true;
      this.gameSettingsService.triggerEmojisPopulated();
    } else {
      alert("Emojis must be 48, Please upload more and try again");
    }
  }

  /**
   * * deleting all emojis on single click
   * @param emoji
   */
  deleteExtraEmojis(emoji?) {
    this.fileNameDialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: "Are you sure you want to Delete?",
      },
    });
    this.fileNameDialogRef
      .afterClosed()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(async (result) => {
        if (result) {
          this.fuseSplashScreenService.show();
          if (emoji) {
            this.emojis = [];
            this.emojis.push(emoji);
          }
          let pageIndex =
            this.paginator.pageIndex === 0 ? 1 : this.paginator.pageIndex + 1;

          try {
            let response = await this.gameSettingsService
              .upsertEmoji(this.emojis, "delete", pageIndex)
              .toPromise();
            if (!response) {
              this.fuseSplashScreenService.hide();
              throw new Error('Error updating emojis, Please try again later!');
            }
            this.gameSettingsService.extraEmojisLength -= this.emojis.length;
            this.emojis =
              typeof response === "object" ? Object.values(response) : response;
            if (this.emojis.length === 0) {
              let getEmojis: any = await this.emojiApi
                .find({
                  where: { isActive: true },
                  order: ["order ASC"],
                  limit: 48,
                })
                .toPromise();
              if (!getEmojis) {
                this.fuseSplashScreenService.hide();
                throw new Error('Error getting emojis, Please try again later!');
              }
              this.emojis = getEmojis;
            }
            let message = this.emojis.length > 1 ? "Emojis" : "Emoji";
            this.fuseSplashScreenService.hide();
            this.snackBar.open(message + " deleted successfully!", "OK", {
              verticalPosition: "top",
              duration: 3000,
            });
          } catch (error) {
            this.fuseSplashScreenService.hide();
            this.snackBar.open(error.message, "ERROR", {
              verticalPosition: "top",
              duration: 3000,
            });
            throw new Error(error.message);
          }
        }
      });
  }

  findDuplicateGroup(emoji: any): string | null {
    const tabs = this.gameSettingsService.currentTabs || [];
    const targetId = String(
      emoji.id || emoji._id || (emoji.emoji && (emoji.emoji.id || emoji.emoji._id))
    );
    for (const tab of tabs) {
      if (!tab || !Array.isArray(tab.screens)) continue;
      for (const screen of tab.screens) {
        if (!screen || !Array.isArray(screen.emojis)) continue;
        const exists = screen.emojis.some((e: any) => {
          if (!e) return false;
          const eId = String(
            e.id || e._id || (e.emoji && (e.emoji.id || e.emoji._id))
          );
          return eId === targetId;
        });
        if (exists) {
          return screen.groupTitle || screen.title || "another Dress Up group";
        }
      }
    }
    return null;
  }

  duplicateEmojiDialog(groupName: string) {
    this.fileNameDialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        isConfirmation: true,
        message: `This emoji is already assigned to ${groupName}. An emoji cannot appear in more than one Dress Up group.`,
      },
    });
  }

  addEmojiToScreens(emoji) {
    if (this.removedEmojis.length !== 0) {
      if (this.gameFormat === "dressUp") {
        const existingGroup = this.findDuplicateGroup(emoji);
        if (existingGroup) {
          this.duplicateEmojiDialog(existingGroup);
          return;
        }
      }

      this.fileNameDialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          title: "Confirmation!", 
          message: "Are you certain about adding this emoji to the vacant space on the screens?",
        },
      });
      this.fileNameDialogRef
        .afterClosed()
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe((result) => {
          if (result) {
            this.gameSettingsService.updateEmojiAdded(emoji);
          }
        });
    } else {
      this.screensFullDialog();
    }
  }

  screensFullDialog() {
    this.fileNameDialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        isConfirmation: true,
        message: "Please remove an emoji from any screen before retrying!",
      },
    });
  }
}
